import React, { useState, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import ParticlesBg from './DemoApp/ParticlesBg';
import './Portfolio.css';
import './BrowserCompat.css';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [visibleSections, setVisibleSections] = useState(new Set(['intro', 'projects', 'resume', 'contact']));
  const sectionRefs = useRef({});
  const lenisRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [transitionSkeleton, setTransitionSkeleton] = useState(null); // Track which section is showing skeleton
  const [scrollVelocity, setScrollVelocity] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  const [visitedSections, setVisitedSections] = useState(new Set(['intro']));
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const touchStartY = useRef(0);
  const scrollRAF = useRef(null);
  const isNavScrolling = useRef(false);
  const [formData, setFormData] = useState({ 
    name: '', email: '', subject: '', message: '', 
    projectType: 'Web Development', budget: 'To be discussed' 
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: null });

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: null });

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.success) {
        setFormStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', subject: '', message: '', projectType: 'Web Development', budget: 'To be discussed' });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setFormStatus({ loading: false, success: false, error: error.message });
    }
  };

  // Fix iOS Safari viewport height and detect mobile
  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    // Detect if mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      smoothTouch: false, // Disable smooth touch for better mobile compatibility
      touchMultiplier: 1,
      infinite: false,
    });

    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Fade transition navigation between sections with skeleton loading
  const scrollToSection = (sectionId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      isNavScrolling.current = true;
      
      // Check if this is the first visit to this section
      const isFirstVisit = !visitedSections.has(sectionId);
      
      // Mark section as visited
      if (isFirstVisit) {
        setVisitedSections(prev => new Set([...prev, sectionId]));
      }
      
      // Get current section
      const currentSection = document.getElementById(activeSection);
      
      // Add fade-out to current section
      if (currentSection && activeSection !== sectionId) {
        currentSection.classList.add('section-fade-out');
      }
      
      // Instant scroll (no smooth scroll)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      
      // Wait for fade out, then switch sections
      setTimeout(() => {
        // Show skeleton only on first visit
        if (isFirstVisit) {
          setTransitionSkeleton(sectionId);
        }
        
        // Instant scroll to new section
        if (isMobile) {
          element.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else if (lenisRef.current) {
          try {
            lenisRef.current.scrollTo(element, { offset: 0, duration: 0, immediate: true });
          } catch (error) {
            element.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        } else {
          element.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        
        // Remove fade-out from previous section
        if (currentSection) {
          currentSection.classList.remove('section-fade-out');
        }
        
        // Show skeleton for proper duration on first visit, or quick fade on repeat visit
        setTimeout(() => {
          if (isFirstVisit) {
            setTransitionSkeleton(null);
          }
          
          // Add fade-in only on repeat visits (not during skeleton)
          if (!isFirstVisit) {
            element.classList.add('section-fade-in');
            
            // Remove fade-in class after animation
            setTimeout(() => {
              element.classList.remove('section-fade-in');
            }, 500);
          }
          
          // Animate content in new section
          const sectionElements = element.querySelectorAll(
            '.project-card, .resume-item, .skill-category, .contact-item, .contact-subtitle, .contact-description, .contact-form, .intro-content, .intro-buttons, .resume-header-section, .download-resume-btn'
          );
          
          // Separately handle section title for immediate visibility
          const sectionTitle = element.querySelector('.section-title');
          if (sectionTitle) {
            sectionTitle.style.opacity = '1';
            sectionTitle.style.transform = 'none';
            sectionTitle.classList.add('animate-in');
            sectionTitle.classList.add('nav-triggered');
          }
          
          sectionElements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('animate-in');
              el.classList.add('nav-triggered');
            }, isFirstVisit ? 50 + (index * 30) : 30 + (index * 30)); // Content appears right after skeleton
          });
          
        }, isFirstVisit ? 350 : 0); // Skeleton duration on first visit, instant on repeat
        
      }, 300); // Wait for premium fade-out
      
      setActiveSection(sectionId);
      
      // Reset nav scrolling flag
      setTimeout(() => {
        isNavScrolling.current = false;
      }, 1000);
    }
  };

  // Skeleton loading with mobile optimization
  useEffect(() => {
    // Faster loading on mobile
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    
    const skeletonTimer = setTimeout(() => {
      setShowSkeleton(false);
    }, isMobileDevice ? 400 : 550);
    
    return () => {
      clearTimeout(skeletonTimer);
    };
  }, []);

  // Touch event handlers for mobile scroll tracking
  useEffect(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    if (!isMobileDevice) return;

    const handleTouchStart = (e) => {
      setIsTouching(true);
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!isTouching) return;
      const touchY = e.touches[0].clientY;
      const delta = Math.abs(touchY - touchStartY.current);
      
      // Update velocity based on touch movement
      if (delta > 5) {
        const velocity = delta / 100;
        setScrollVelocity(Math.min(velocity, 2));
      }
    };

    const handleTouchEnd = () => {
      setIsTouching(false);
      // Decay velocity quickly after touch ends
      setTimeout(() => setScrollVelocity(0), 150);
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isTouching]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    // Wait until skeleton is hidden before setting up animations
    if (showSkeleton) return;

    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

    const observerOptions = {
      root: null,
      rootMargin: isMobileDevice ? '0px 0px -8% 0px' : '0px 0px -10% 0px', // Better trigger point for mobile
      threshold: isMobileDevice ? [0.05, 0.1, 0.2, 0.3] : 0.15 // Multiple thresholds for smoother mobile tracking
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Use same nav-triggered animation for scroll
          entry.target.classList.add('animate-in');
          entry.target.classList.add('nav-triggered');
          
          // On mobile, add extra smoothness class
          if (isMobileDevice && !entry.target.classList.contains('smooth-mobile')) {
            entry.target.classList.add('smooth-mobile');
          }
          
          // Unobserve after animation to improve performance
          setTimeout(() => {
            observer.unobserve(entry.target);
          }, isMobileDevice ? 700 : 500);
        }
      });
    }, observerOptions);

    // Small delay to ensure DOM is ready
    const setupTimer = setTimeout(() => {
      const animatableElements = document.querySelectorAll(
        '.project-card, .resume-item, .skill-category, .contact-item, .contact-subtitle, .contact-description, .contact-form, .intro-content:not(.skeleton-intro), .section-title, .intro-buttons, .resume-header-section, .download-resume-btn'
      );
      
      // Group elements by section for staggered animation
      const sections = {};
      animatableElements.forEach(el => {
        const section = el.closest('.section')?.id || 'default';
        if (!sections[section]) sections[section] = [];
        sections[section].push(el);
      });

      // Apply staggered delays within each section
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      const staggerDelay = isMobileDevice ? 0.08 : 0.04; // Slower stagger on mobile
      
      Object.values(sections).forEach(sectionElements => {
        sectionElements.forEach((el, index) => {
          el.style.transitionDelay = `${index * staggerDelay}s`;
          observer.observe(el);
        });
      });
    }, isMobileDevice ? 50 : 30);

    return () => {
      clearTimeout(setupTimer);
    };
  }, [showSkeleton]);

  // Track scroll velocity for dynamic animations with mobile optimization
  useEffect(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    let ticking = false;
    let velocityTimeout;

    const calculateVelocity = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const timeDelta = currentTime - lastScrollTime.current;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);
      
      if (timeDelta > 0) {
        const velocity = scrollDelta / timeDelta;
        // Smoother velocity tracking on mobile
        const smoothedVelocity = isMobileDevice ? velocity * 0.7 : velocity;
        setScrollVelocity(smoothedVelocity);
        
        // Longer decay on mobile for smoother feel
        clearTimeout(velocityTimeout);
        velocityTimeout = setTimeout(() => {
          setScrollVelocity(0);
        }, isMobileDevice ? 200 : 100);
      }
      
      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          calculateVelocity();
          
          const sections = ['intro', 'projects', 'resume', 'contact'];
          const scrollPosition = window.scrollY + window.innerHeight / 2;
          const newVisibleSections = new Set(['intro']); // Always show intro

          for (const section of sections) {
            const element = document.getElementById(section);
            if (element) {
              const { offsetTop, offsetHeight } = element;
              
              // Check if section is in viewport (more generous threshold)
              if (scrollPosition >= offsetTop - 300 && scrollPosition < offsetTop + offsetHeight + 300) {
                newVisibleSections.add(section);
              }
              
              // Update active section
              if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                setActiveSection(section);
              }
            }
          }

          setVisibleSections(newVisibleSections);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(velocityTimeout);
    };
  }, []);

  return (
    <div className="portfolio loaded">
      <ParticlesBg
        backgroundColors="linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(236, 72, 153, 0.9) 100%)"
        density={isMobile ? 30 : 80}
        dotColor="#ffffff"
        lineColor="rgba(255, 255, 255, 0.3)"
        particleRadius={isMobile ? 3 : 4}
        lineWidth={1}
        proximity={isMobile ? 100 : 140}
        minSpeed={0.5}
        maxSpeed={isMobile ? 1.5 : 2}
        createOnClick={isMobile ? 3 : 8}
        repulse={isMobile ? 80 : 120}
      />
      
      {/* Navigation */}
      <nav className="navbar" data-lenis-prevent>
        <button 
          className={`nav-link ${activeSection === 'intro' ? 'active' : ''}`}
          onClick={(e) => scrollToSection('intro', e)}
          type="button"
        >
          Intro
        </button>
        <button 
          className={`nav-link ${activeSection === 'projects' ? 'active' : ''}`}
          onClick={(e) => scrollToSection('projects', e)}
          type="button"
        >
          Projects
        </button>
        <button 
          className={`nav-link ${activeSection === 'resume' ? 'active' : ''}`}
          onClick={(e) => scrollToSection('resume', e)}
          type="button"
        >
          Resume
        </button>
        <button 
          className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
          onClick={(e) => scrollToSection('contact', e)}
          type="button"
        >
          Contact
        </button>
      </nav>

      {/* Intro Section */}
      <section 
        id="intro" 
        className={`section intro-section ${visibleSections.has('intro') ? 'visible' : ''}`}
        ref={el => sectionRefs.current.intro = el}
      >
        <div className="container">
          {transitionSkeleton === 'intro' && (
            <div className="transition-skeleton-overlay">
              <div className="intro-content skeleton-intro">
                <div className="skeleton-text-content">
                  <div className="skeleton skeleton-title"></div>
                  <div className="skeleton skeleton-subtitle"></div>
                  <div className="skeleton skeleton-line"></div>
                  <div className="skeleton skeleton-line"></div>
                  <div className="skeleton skeleton-line short"></div>
                  <div className="skeleton-buttons">
                    <div className="skeleton skeleton-button"></div>
                    <div className="skeleton skeleton-button"></div>
                  </div>
                </div>
                <div className="skeleton-image-content">
                  <div className="skeleton skeleton-circle"></div>
                </div>
              </div>
            </div>
          )}
          {showSkeleton ? (
            <div className="intro-content skeleton-intro">
              <div className="skeleton-text-content">
                <div className="skeleton skeleton-title"></div>
                <div className="skeleton skeleton-subtitle"></div>
                <div className="skeleton skeleton-line"></div>
                <div className="skeleton skeleton-line"></div>
                <div className="skeleton skeleton-line short"></div>
                <div className="skeleton-buttons">
                  <div className="skeleton skeleton-button"></div>
                  <div className="skeleton skeleton-button"></div>
                </div>
              </div>
              <div className="skeleton-image-content">
                <div className="skeleton skeleton-circle"></div>
              </div>
            </div>
          ) : (
          <div className="intro-content content-loaded">
            <div className="intro-text-content">
              <h1 className="intro-title">
                Hi, I'm <span className="highlight">Saroj Bhandari</span>
              </h1>
              <h2 className="intro-subtitle">Full Stack Developer</h2>
              <div className="intro-description">
                <ul className="intro-highlights">
                  <li><span className="icon">●</span> Bachelor of IT Graduate</li>
                  <li><span className="icon">●</span> React • Django • PHP</li>
                  <li><span className="icon">●</span> Team Lead - MRI System</li>
                  <li><span className="icon">●</span> OpenAI & Payment Integration</li>
                </ul>
              </div>
              <div className="intro-buttons">
                <button 
                  className="btn btn-primary"
                  onClick={() => scrollToSection('projects')}
                >
                  View My Work
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => scrollToSection('contact')}
                >
                  Get In Touch
                </button>
              </div>
            </div>
            <div className="intro-image">
              <div className="profile-image">
                <img 
                  src="/profile-image.jpg" 
                  alt="Saroj Bhandari" 
                  className="profile-photo"
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Projects Section */}
      <section 
        id="projects" 
        className={`section projects-section ${visibleSections.has('projects') ? 'visible' : ''}`}
        ref={el => sectionRefs.current.projects = el}
      >
        <div className="container">
          {transitionSkeleton === 'projects' && (
            <div className="transition-skeleton-overlay">
              <div className="skeleton skeleton-section-title"></div>
              <div className="projects-grid">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-card">
                    <div className="skeleton skeleton-card-image"></div>
                    <div className="skeleton-card-content">
                      <div className="skeleton skeleton-card-title" style={{marginBottom: '0.75rem'}}></div>
                      <div className="skeleton skeleton-line" style={{marginBottom: '0.5rem'}}></div>
                      <div className="skeleton skeleton-line" style={{marginBottom: '0.5rem'}}></div>
                      <div className="skeleton skeleton-line short" style={{marginBottom: '1rem'}}></div>
                      <div className="skeleton-tags" style={{marginBottom: '1rem'}}>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                      </div>
                      <div style={{display: 'flex', gap: '1rem'}}>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '20px'}}></div>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '20px'}}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showSkeleton ? (
            <>
              <div className="skeleton skeleton-section-title"></div>
              <div className="projects-grid">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-card">
                    <div className="skeleton skeleton-card-image"></div>
                    <div className="skeleton-card-content">
                      <div className="skeleton skeleton-card-title" style={{marginBottom: '0.75rem'}}></div>
                      <div className="skeleton skeleton-line" style={{marginBottom: '0.5rem'}}></div>
                      <div className="skeleton skeleton-line" style={{marginBottom: '0.5rem'}}></div>
                      <div className="skeleton skeleton-line short" style={{marginBottom: '1rem'}}></div>
                      <div className="skeleton-tags" style={{marginBottom: '1rem'}}>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                        <div className="skeleton skeleton-tag"></div>
                      </div>
                      <div style={{display: 'flex', gap: '1rem'}}>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '20px'}}></div>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '20px'}}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid">
            <a href="https://previo.com.au" target="_blank" rel="noopener noreferrer" className="project-card-link">
              <div className="project-card">
                <div className="project-image">
                  <img
                    src="/previo-logo.svg"
                    alt="Previo - Smart Inventory Platform for Cafes"
                  />
                </div>
                <div className="project-content">
                  <h3 className="project-title">
                    Previo - Smart Inventory &amp; Supplier Platform
                  </h3>
                  <p className="project-description">
                    Founded and built a full-stack SaaS platform that stops independent Melbourne cafes losing
                    ~$500/week to over-ordering and waste. Connects to Square POS, learns day-of-week and seasonal
                    sales patterns, and tells operators exactly what to order and when. Shipped inventory &amp; supplier
                    management, a Gmail-integrated email client, AI-powered invoice extraction, a pattern-recognition
                    ordering engine, staff rostering, and Stripe billing. 296 commits, live in production with
                    CI/CD on every push.
                  </p>
                  <div className="project-tech">
                    <span className="tech-tag">Next.js 16</span>
                    <span className="tech-tag">Supabase</span>
                    <span className="tech-tag">Tailwind CSS v4</span>
                    <span className="tech-tag">Square API</span>
                    <span className="tech-tag">Stripe</span>
                    <span className="tech-tag">Claude AI</span>
                  </div>
                  <div className="project-links">
                    <span className="project-link">Live Site</span>
                  </div>
                </div>
              </div>
            </a>

            <a href="https://public-website-swart.vercel.app" target="_blank" rel="noopener noreferrer" className="project-card-link">
              <div className="project-card">
                <div className="project-image">
                  <img
                    src="/capital-radiology-mri.jpg.png"
                    alt="Capital Radiology MRI Management System"
                  />
                </div>
                <div className="project-content">
                  <h3 className="project-title">
                    Capital Radiology - MRI Management System
                  </h3>
                  <p className="project-description">
                    Led a team of 6 developers to create a complete web-based MRI management system for Capital Radiology. 
                    Implemented secure authentication, OpenAI chatbot integration, and payment systems with Stripe/PayPal.
                  </p>
                  <div className="project-tech">
                    <span className="tech-tag">React</span>
                    <span className="tech-tag">Django</span>
                    <span className="tech-tag">OpenAI API</span>
                    <span className="tech-tag">Stripe</span>
                  </div>
                  <div className="project-links">
                    <span className="project-link">Live Demo</span>
                    <a href="https://github.com/M325-MRI/Assessment6" target="_blank" rel="noopener noreferrer" className="project-link" onClick={(e) => e.stopPropagation()}>GitHub</a>
                  </div>
                </div>
              </div>
            </a>

            <a href="https://turnitin-mock.vercel.app/landing" target="_blank" rel="noopener noreferrer" className="project-card-link">
              <div className="project-card">
                <div className="project-image">
                  <img
                    src="/verity-project.png"
                    alt="Verity Academic Integrity Detection Platform"
                  />
                </div>
                <div className="project-content">
                  <h3 className="project-title">Verity - Academic Integrity Detection</h3>
                  <p className="project-description">
                    Built a Turnitin-style academic integrity platform that scores submissions across three signal
                    tracks (human authenticity, AI-slop language patterns, and behavioural risk from paste events,
                    editing time, and revision count) to flag AI-generated coursework with a confidence-rated verdict.
                  </p>
                  <div className="project-tech">
                    <span className="tech-tag">Next.js 15</span>
                    <span className="tech-tag">TypeScript</span>
                    <span className="tech-tag">Tailwind CSS 4</span>
                  </div>
                  <div className="project-links">
                    <span className="project-link">Live Demo</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
            </>
          )}
        </div>
      </section>

      {/* Resume Section */}
      <section 
        id="resume" 
        className={`section resume-section ${visibleSections.has('resume') ? 'visible' : ''}`}
        ref={el => sectionRefs.current.resume = el}
      >
        <div className="container">
          {transitionSkeleton === 'resume' && (
            <div className="transition-skeleton-overlay">
              <div className="resume-header-section" style={{width: '100%', marginBottom: '3rem'}}>
                <div className="skeleton skeleton-section-title" style={{margin: '0 auto 0 0'}}></div>
                <div className="skeleton skeleton-button" style={{width: '180px', height: '50px'}}></div>
              </div>
              <div className="resume-content" style={{width: '100%', maxWidth: '1200px'}}>
                <div className="resume-column">
                  <div className="skeleton skeleton-subtitle" style={{width: '150px', height: '30px', marginBottom: '1.5rem'}}></div>
                  <div className="skeleton-card" style={{maxWidth: '100%', marginBottom: '1rem'}}>
                    <div className="skeleton-card-content">
                      <div className="skeleton skeleton-card-title"></div>
                      <div className="skeleton skeleton-line"></div>
                      <div className="skeleton skeleton-line"></div>
                      <div className="skeleton skeleton-line short"></div>
                    </div>
                  </div>
                  <div className="skeleton skeleton-subtitle" style={{width: '120px', height: '30px', margin: '2rem 0 1.5rem'}}></div>
                  <div className="skeleton-card" style={{maxWidth: '100%'}}>
                    <div className="skeleton-card-content">
                      <div className="skeleton skeleton-card-title"></div>
                      <div className="skeleton skeleton-line"></div>
                      <div className="skeleton skeleton-line short"></div>
                    </div>
                  </div>
                </div>
                <div className="resume-column">
                  <div className="skeleton skeleton-subtitle" style={{width: '100px', height: '30px', marginBottom: '1.5rem'}}></div>
                  <div className="skills-grid" style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(2, 1fr)'}}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="skeleton-card" style={{maxWidth: '100%'}}>
                        <div className="skeleton-card-content">
                          <div className="skeleton skeleton-card-title" style={{marginBottom: '0.75rem'}}></div>
                          <div className="skeleton-tags">
                            <div className="skeleton skeleton-tag"></div>
                            <div className="skeleton skeleton-tag"></div>
                            <div className="skeleton skeleton-tag"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!showSkeleton && (
            <>
          <div className="resume-header-section">
            <h2 className="section-title">Resume</h2>
            <a
              className="download-resume-btn"
              href="/finalsarojresume.pdf"
              download="Saroj_Bhandari_Resume.pdf"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
              Download PDF
            </a>
          </div>
          <div className="resume-content">
            <div className="resume-column">
              <h3 className="resume-subtitle">Experience</h3>
              <div className="resume-item">
                <div className="resume-header">
                  <h4 className="resume-position">Founding Engineer & Sole Developer</h4>
                  <span className="resume-company">Previo</span>
                  <span className="resume-date">2025 - Present</span>
                </div>
                <p className="resume-description">
                  Designed and built Previo, a production SaaS inventory and supplier management platform for cafes, end-to-end
                  and solo, covering data model, auth, billing, and every customer-facing feature, from first commit to a live,
                  paying-customer-ready platform. Built a proprietary demand-forecasting engine (day-of-week seasonality,
                  trend detection, holiday and weather multipliers) that turns raw Square sales into re-order suggestions
                  with no ML infrastructure required. Integrated Square POS, Stripe billing, and a full in-app Gmail OAuth
                  email client with AI-drafted replies and AI-assisted invoice extraction. Enforced Postgres Row-Level
                  Security across every multi-tenant table and hardened OAuth flows, webhook signatures, and AI endpoints
                  against SSRF and abuse.
                </p>
              </div>

              <div className="education">
                <h3 className="resume-subtitle">Education</h3>
                <div className="resume-item">
                  <div className="resume-header">
                    <h4 className="resume-position">Bachelor of Information Technology</h4>
                    <span className="resume-company">Kent Institute Australia</span>
                    <span className="resume-date">2023 - 2025</span>
                  </div>
                  <p className="resume-description">
                    Strong foundation in full-stack software development with a focus on clean, efficient, and secure code.
                    Completed a Capstone Project building a full web-based MRI management system for Capital Radiology.
                  </p>
                </div>
              </div>
            </div>

            <div className="resume-column">
              <h3 className="resume-subtitle">Skills</h3>
              <div className="skills-grid">
                <div className="skill-category">
                  <h4 className="skill-title">Languages & Frontend</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">TypeScript</span>
                    <span className="skill-tag">Next.js</span>
                    <span className="skill-tag">React</span>
                    <span className="skill-tag">Tailwind CSS</span>
                  </div>
                </div>

                <div className="skill-category">
                  <h4 className="skill-title">Backend & Database</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">Node.js</span>
                    <span className="skill-tag">PostgreSQL</span>
                    <span className="skill-tag">Supabase</span>
                    <span className="skill-tag">REST APIs</span>
                  </div>
                </div>

                <div className="skill-category">
                  <h4 className="skill-title">Auth & Security</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">OAuth 2.0</span>
                    <span className="skill-tag">Row-Level Security</span>
                    <span className="skill-tag">Zod</span>
                    <span className="skill-tag">Rate Limiting</span>
                  </div>
                </div>

                <div className="skill-category">
                  <h4 className="skill-title">Integrations</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">Stripe</span>
                    <span className="skill-tag">Square POS</span>
                    <span className="skill-tag">Gmail API</span>
                    <span className="skill-tag">Xero</span>
                  </div>
                </div>

                <div className="skill-category">
                  <h4 className="skill-title">AI Engineering</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">Claude</span>
                    <span className="skill-tag">LLM Integration</span>
                    <span className="skill-tag">Prompt Safety</span>
                    <span className="skill-tag">AI Data Extraction</span>
                  </div>
                </div>

                <div className="skill-category">
                  <h4 className="skill-title">DevOps & Tooling</h4>
                  <div className="skill-tags">
                    <span className="skill-tag">Vercel</span>
                    <span className="skill-tag">Docker</span>
                    <span className="skill-tag">Git</span>
                    <span className="skill-tag">Sentry</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact" 
        className={`section contact-section ${visibleSections.has('contact') ? 'visible' : ''}`}
        ref={el => sectionRefs.current.contact = el}
      >
        <div className="container">
          {transitionSkeleton === 'contact' && (
            <div className="transition-skeleton-overlay">
              <div className="skeleton skeleton-section-title"></div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', width: '100%', maxWidth: '1200px', margin: '0 auto'}}>
                <div>
                  <div className="skeleton skeleton-subtitle" style={{width: '250px', height: '32px', marginBottom: '1rem'}}></div>
                  <div className="skeleton skeleton-line" style={{width: '90%', marginBottom: '0.75rem'}}></div>
                  <div className="skeleton skeleton-line" style={{width: '85%', marginBottom: '2rem'}}></div>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{display: 'flex', gap: '1rem', marginBottom: '1.5rem'}}>
                      <div className="skeleton skeleton-circle" style={{width: '50px', height: '50px', flexShrink: 0}}></div>
                      <div style={{flex: 1}}>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '18px', marginBottom: '0.5rem'}}></div>
                        <div className="skeleton skeleton-line" style={{width: '160px', height: '16px'}}></div>
                      </div>
                    </div>
                  ))}
                  <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                    <div className="skeleton skeleton-button" style={{width: '130px', height: '45px'}}></div>
                    <div className="skeleton skeleton-button" style={{width: '130px', height: '45px'}}></div>
                  </div>
                </div>
                <div className="skeleton-card" style={{maxWidth: '100%', minHeight: '500px'}}>
                  <div className="skeleton-card-content">
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{height: '140px', marginBottom: '1.5rem'}}></div>
                    <div className="skeleton skeleton-button" style={{width: '100%', height: '50px'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showSkeleton ? (
            <>
              <div className="skeleton skeleton-section-title"></div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', width: '100%', maxWidth: '1200px', margin: '0 auto'}}>
                <div>
                  <div className="skeleton skeleton-subtitle" style={{width: '250px', height: '32px', marginBottom: '1rem'}}></div>
                  <div className="skeleton skeleton-line" style={{width: '90%', marginBottom: '0.75rem'}}></div>
                  <div className="skeleton skeleton-line" style={{width: '85%', marginBottom: '2rem'}}></div>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{display: 'flex', gap: '1rem', marginBottom: '1.5rem'}}>
                      <div className="skeleton skeleton-circle" style={{width: '50px', height: '50px', flexShrink: 0}}></div>
                      <div style={{flex: 1}}>
                        <div className="skeleton skeleton-line" style={{width: '80px', height: '18px', marginBottom: '0.5rem'}}></div>
                        <div className="skeleton skeleton-line" style={{width: '160px', height: '16px'}}></div>
                      </div>
                    </div>
                  ))}
                  <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                    <div className="skeleton skeleton-button" style={{width: '130px', height: '45px'}}></div>
                    <div className="skeleton skeleton-button" style={{width: '130px', height: '45px'}}></div>
                  </div>
                </div>
                <div className="skeleton-card" style={{maxWidth: '100%', minHeight: '500px'}}>
                  <div className="skeleton-card-content">
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{marginBottom: '1.5rem', height: '45px'}}></div>
                    <div className="skeleton skeleton-line" style={{height: '140px', marginBottom: '1.5rem'}}></div>
                    <div className="skeleton skeleton-button" style={{width: '100%', height: '50px'}}></div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{opacity: 1, visibility: 'visible', transition: 'opacity 0.3s ease, visibility 0.3s ease'}}>
          <h2 className="section-title">Collaboration Portal</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="availability-badge">
                ● Currently Available for New Projects
              </div>
              <h3 className="contact-subtitle">Let's build something extraordinary.</h3>
              <p className="contact-description">
                Whether you're looking for a full-stack developer for a long-term project or a specialist for a quick integration, I'm here to help you scale your technical vision.
              </p>

              <div className="contact-details">
                <div className="contact-item" onClick={() => { navigator.clipboard.writeText('bhandarisaroj642@gmail.com'); alert('Email copied to clipboard!'); }}>
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h4>Email (Click to copy)</h4>
                    <p>bhandarisaroj642@gmail.com</p>
                  </div>
                </div>

                <div className="contact-item" onClick={() => { navigator.clipboard.writeText('saroj.bhandari@previo.com.au'); alert('Email copied to clipboard!'); }}>
                  <div className="contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h4>Work Email (Click to copy)</h4>
                    <p>saroj.bhandari@previo.com.au</p>
                  </div>
                </div>
              </div>

              <div className="social-links">
                <a href="https://www.linkedin.com/in/saroj-bhandari-3b806636a" className="social-link" target="_blank" rel="noopener noreferrer">
                  LinkedIn Profile
                </a>
                <a href="https://github.com/Sarojkent" className="social-link" target="_blank" rel="noopener noreferrer">
                  Technical Portfolio (GitHub)
                </a>
              </div>
            </div>

            <div className="contact-form">
              <form className="form" onSubmit={handleFormSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <input type="text" name="name" className="form-input" placeholder="Name" value={formData.name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-group">
                    <input type="email" name="email" className="form-input" placeholder="Work Email" value={formData.email} onChange={handleFormChange} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <select name="projectType" className="form-input" value={formData.projectType} onChange={handleFormChange}>
                      <option value="Web Development">Full-Stack Web App</option>
                      <option value="Mobile Development">Mobile App</option>
                      <option value="API Integration">API / OpenAI Integration</option>
                      <option value="Maintenance">System Maintenance</option>
                      <option value="Consulting">Consulting</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <select name="budget" className="form-input" value={formData.budget} onChange={handleFormChange}>
                      <option value="To be discussed">Budget: To be discussed</option>
                      <option value="$1k - $5k">$1k - $5k</option>
                      <option value="$5k - $10k">$5k - $10k</option>
                      <option value="$10k+">$10k+</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <input type="text" name="subject" className="form-input" placeholder="Project Subject" value={formData.subject} onChange={handleFormChange} required />
                </div>

                <div className="form-group">
                  <textarea name="message" className="form-input form-textarea" placeholder="Describe your technical requirements..." rows="4" value={formData.message} onChange={handleFormChange} required></textarea>
                </div>

                <button type="submit" className="btn btn-primary form-submit" disabled={formStatus.loading}>
                  {formStatus.loading ? 'Transmitting...' : 'Send Inquiry'}
                </button>

                {formStatus.success && <div className="form-success">✓ Your inquiry has been prioritized. I will contact you shortly.</div>}
                {formStatus.error && <div className="form-error">❌ Transmission error: {formStatus.error}</div>}
              </form>
            </div>
          </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className={`footer ${visibleSections.has('contact') ? 'visible' : ''}`}>
        <div className="container">
          <p>© {new Date().getFullYear()} Saroj Bhandari. Built with React.</p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
